import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { PGIAssessmentMapping } from "../../interfaces/PGIAssessmentMapping";
import {
  computeSankey,
  LaidOutLink,
  RawLink,
  RawNode,
} from "./sankeyLayout";

// Lighter stage colours (slate -> blue -> teal -> lime) so dark labels stay legible
// where they overlap the ribbons.
const COLORS: Record<number, string> = {
  0: "#90A4B4", // Answer / Score
  1: "#6E9CC4", // Problem
  2: "#8E94D6", // Goal
  3: "#9DC74E", // Intervention
};
const STAGE_LABEL: Record<number, string> = {
  0: "ANSWER",
  1: "PROBLEM",
  2: "GOAL",
  3: "INTERVENTION",
};

function truncate(s: string, n: number): string {
  return s && s.length > n ? s.slice(0, n - 1) + "…" : s;
}

// Measure the container's width so the chart fills it (capped for very wide screens).
function useContainerWidth(): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(960);
  useLayoutEffect(() => {
    if (!ref.current) return;
    // Fill the full container width (floored so very narrow panes stay usable).
    const measure = () => setWidth(Math.max(ref.current!.clientWidth || 960, 720));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

interface TooltipState {
  text: string;
  x: number;
  y: number;
}

interface Props {
  rows: PGIAssessmentMapping[];
}

export const PGISankey: React.FC<Props> = ({ rows }) => {
  const [ref, width] = useContainerWidth();
  const [hoverActive, setHoverActive] = useState<Set<number> | null>(null);
  const [lockedActive, setLockedActive] = useState<Set<number> | null>(null);
  const [tip, setTip] = useState<TooltipState | null>(null);

  // Build de-duplicated nodes (by stage+name) and links (by source->target).
  const { rawNodes, rawLinks } = useMemo(() => {
    const nodeIndex = new Map<string, number>();
    const rawNodes: RawNode[] = [];
    const nodeId = (stage: number, name: string) => {
      const key = stage + "|" + name;
      let i = nodeIndex.get(key);
      if (i === undefined) {
        i = rawNodes.length;
        nodeIndex.set(key, i);
        rawNodes.push({ stage, label: name, full: name });
      }
      return i;
    };
    const linkMap = new Map<string, number>();
    const rawLinks: RawLink[] = [];
    const addLink = (s: number, t: number) => {
      const key = s + "->" + t;
      const i = linkMap.get(key);
      if (i === undefined) {
        linkMap.set(key, rawLinks.length);
        rawLinks.push({ source: s, target: t, value: 1 });
      } else {
        rawLinks[i].value += 1;
      }
    };
    rows.forEach((r) => {
      const a = nodeId(0, r.answer);
      const p = nodeId(1, r.problemName);
      const g = nodeId(2, r.goalName);
      const iv = nodeId(3, r.interventionName);
      addLink(a, p);
      addLink(p, g);
      addLink(g, iv);
    });
    return { rawNodes, rawLinks };
  }, [rows]);

  const margin = { top: 32, right: 28, bottom: 16, left: 150 };
  const nodeWidth = 16;
  // Taller rows -> larger vertical scale -> thicker ribbons (esp. goal->intervention,
  // which carry the least weight). Costs a little vertical space, as expected.
  const rowH = 40;
  const maxStageCount = Math.max(
    ...[0, 1, 2, 3].map((s) => rawNodes.filter((n) => n.stage === s).length),
    1,
  );
  const height = Math.max(140, maxStageCount * rowH + margin.top + margin.bottom);

  const { nodes, links } = useMemo(
    () =>
      computeSankey(rawNodes, rawLinks, {
        width,
        height,
        margin,
        nodeWidth,
        nodePadding: 14,
        fractions: { 0: 0, 1: 0.11, 2: 0.32, 3: 0.6 },
      }),
    [rawNodes, rawLinks, width, height],
  );

  // adjacency for path tracing (upstream + downstream)
  const adjacency = useMemo(() => {
    const out = new Map<number, LaidOutLink[]>();
    const inc = new Map<number, LaidOutLink[]>();
    links.forEach((l) => {
      (out.get(l.source) ?? out.set(l.source, []).get(l.source)!).push(l);
      (inc.get(l.target) ?? inc.set(l.target, []).get(l.target)!).push(l);
    });
    return { out, inc };
  }, [links]);

  const pathLinks = (seeds: number[]): Set<number> => {
    const set = new Set<number>();
    const down = (n: number) =>
      (adjacency.out.get(n) ?? []).forEach((l) => {
        if (!set.has(l.index)) {
          set.add(l.index);
          down(l.target);
        }
      });
    const up = (n: number) =>
      (adjacency.inc.get(n) ?? []).forEach((l) => {
        if (!set.has(l.index)) {
          set.add(l.index);
          up(l.source);
        }
      });
    seeds.forEach((n) => {
      down(n);
      up(n);
    });
    return set;
  };

  const active = lockedActive ?? hoverActive;
  const activeNodes = useMemo(() => {
    if (!active) return null;
    const s = new Set<number>();
    links.forEach((l) => {
      if (active.has(l.index)) {
        s.add(l.source);
        s.add(l.target);
      }
    });
    return s;
  }, [active, links]);

  const linkOpacity = (l: LaidOutLink) =>
    !active ? 0.4 : active.has(l.index) ? 0.85 : 0.06;
  const nodeOpacity = (i: number) =>
    !activeNodes ? 1 : activeNodes.has(i) ? 1 : 0.22;

  // dynamic label budget (px -> chars) so labels use available width
  const innerW = width - margin.right - margin.left;
  const colX0: Record<number, number> = {
    0: margin.left,
    1: margin.left + 0.11 * innerW,
    2: margin.left + 0.32 * innerW,
    3: margin.left + 0.6 * innerW,
  };
  const charsFor = (stage: number) => {
    const budget =
      stage === 0
        ? margin.left - 14
        : stage === 3
        ? width - (colX0[3] + nodeWidth) - 14
        : colX0[stage + 1] - (colX0[stage] + nodeWidth) - 14;
    return Math.max(6, Math.floor(budget / 6.6));
  };

  const showTip = (text: string, e: React.MouseEvent) =>
    setTip({ text, x: e.clientX, y: e.clientY });

  const onLeave = () => {
    if (!lockedActive) setHoverActive(null);
    setTip(null);
  };
  const toggleLock = (seeds: number[], e: React.MouseEvent) => {
    e.stopPropagation();
    const next = pathLinks(seeds);
    // clicking the same seed again clears the lock
    setLockedActive((prev) =>
      prev && sameSet(prev, next) ? null : next,
    );
  };

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ display: "block" }}
        onClick={() => setLockedActive(null)}
      >
        {/* column headers */}
        {[0, 1, 2, 3].map((s) => (
          <text
            key={s}
            x={s === 0 ? margin.left - 8 : colX0[s] + nodeWidth + 6}
            y={margin.top - 13}
            textAnchor={s === 0 ? "end" : "start"}
            style={{
              fontSize: 10,
              letterSpacing: "0.05em",
              fill: "#9ca3af",
              fontWeight: 600,
            }}
          >
            {STAGE_LABEL[s]}
          </text>
        ))}

        {/* links */}
        <g fill="none">
          {links.map((l) => (
            <path
              key={l.index}
              d={l.path}
              stroke={COLORS[l.sourceStage]}
              strokeWidth={Math.max(1.5, l.width)}
              strokeOpacity={linkOpacity(l)}
              style={{ cursor: "pointer", transition: "stroke-opacity .12s" }}
              onMouseOver={() => {
                if (!lockedActive) setHoverActive(pathLinks([l.source, l.target]));
              }}
              onMouseMove={(e) =>
                showTip(`${nodes[l.source].full} → ${nodes[l.target].full}`, e)
              }
              onMouseOut={onLeave}
              onClick={(e) => toggleLock([l.source, l.target], e)}
            />
          ))}
        </g>

        {/* nodes */}
        {nodes.map((n) => (
          <g
            key={n.index}
            style={{ opacity: nodeOpacity(n.index), transition: "opacity .12s" }}
          >
            <rect
              x={n.x0}
              y={n.y0}
              width={n.x1 - n.x0}
              height={Math.max(1, n.y1 - n.y0)}
              fill={COLORS[n.stage]}
              stroke="#fff"
              style={{ cursor: "pointer" }}
              onMouseOver={() => {
                if (!lockedActive) setHoverActive(pathLinks([n.index]));
              }}
              onMouseMove={(e) => showTip(n.full, e)}
              onMouseOut={onLeave}
              onClick={(e) => toggleLock([n.index], e)}
            />
            <text
              x={n.stage === 0 ? n.x0 - 8 : n.x1 + 8}
              y={(n.y0 + n.y1) / 2}
              dy="0.35em"
              textAnchor={n.stage === 0 ? "end" : "start"}
              style={{ fontSize: 12.5, fill: "#1f2933", pointerEvents: "none" }}
            >
              {truncate(n.label, charsFor(n.stage))}
            </text>
          </g>
        ))}
      </svg>

      {tip && (
        <div
          style={{
            position: "fixed",
            left: tip.x + 12,
            top: tip.y + 12,
            background: "rgba(6,39,64,0.80)",
            color: "#fff",
            padding: "6px 8px",
            borderRadius: 4,
            fontSize: 12,
            maxWidth: 360,
            pointerEvents: "none",
            zIndex: 1050,
          }}
        >
          {tip.text}
        </div>
      )}
    </div>
  );
};

function sameSet(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) return false;
  return Array.from(a).every((x) => b.has(x));
}

export default PGISankey;
